import { useEffect, useState } from "react";
import { Alert, CreateAlertRequest, UpdateAlertRequest } from "../Types/alert";
import useFetch from "./useFetchAPI";

const useAlerts = () => {
  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [currentAlert, setCurrentAlert] = useState<
    Alert | CreateAlertRequest | null
  >(null);

  // Use the useFetch hook for API calls
  const {
    fetchAPI: fetchAlertsAPI,
    apiData: alertsData,
    isLoading: isLoadingAlerts,
    serverError: serverErrorAlerts,
  } = useFetch({ scope: "AppUser" });
  const {
    fetchAPI: updateAlertAPI,
    apiData: updateAlertData,
    isLoading: isUpdatingAlert,
    serverError: serverErrorUpdateAlert,
  } = useFetch({ scope: "AppUser" });
  const {
    fetchAPI: createAlertAPI,
    apiData: createAlertData,
    isLoading: isCreatingAlert,
    serverError: serverErrorCreateAlert,
  } = useFetch({ scope: "AppUser" });
  const {
    fetchAPI: deleteAlertAPI,
    apiData: deleteAlertData,
    isLoading: isDeletingAlert,
    serverError: serverErrorDeleteAlert,
  } = useFetch({ scope: "AppUser" });

  const addAlert = (alert: Alert) => {
    setAlerts((prevAlerts) => [...prevAlerts, alert]);
  };

  const removeAlert = (id: number) => {
    setAlerts((prevAlerts) =>
      prevAlerts.filter((alert) => alert.AlertId !== id)
    );
  };

  const updateAlert = async (
    updatedAlert: UpdateAlertRequest
  ): Promise<void> => {
    await updateAlertAPI({
      url: `alerts/update`,
      method: "PUT",
      body: updatedAlert as unknown as Record<string, unknown>,
    });

    // Update the alert in the local state
    setAlerts((prevAlerts) =>
      prevAlerts.map((alert) =>
        alert.AlertId === updatedAlert.AlertId
          ? { ...alert, ...updatedAlert, LastUpdatedAt: new Date() }
          : alert
      )
    );
  };

  const createAlert = async (newAlert: CreateAlertRequest) => {
    await createAlertAPI({
      url: `alerts/create`,
      method: "POST",
      body: newAlert as unknown as Record<string, unknown>,
    });
  };

  useEffect(() => {
    if (createAlertData && (createAlertData as any).alert) {
      const newAlertData = (createAlertData as any).alert as Alert;
      addAlert(newAlertData);
    }
  }, [createAlertData]);

  const fetchAlerts = async (): Promise<void> => {
    await fetchAlertsAPI({
      url: `alerts`,
      method: "GET",
    });

    // The response data will be in alertsData
    if (alertsData && (alertsData as any).alerts) {
      setAlerts((alertsData as any).alerts);
    }
  };

  const deleteAlert = async (alertId: number): Promise<void> => {
    await deleteAlertAPI({
      url: `alerts/delete`,
      method: "DELETE",
      body: { AlertId: alertId } as unknown as Record<string, unknown>,
    });

    removeAlert(alertId);
  };

  return {
    alerts,
    currentAlert,
    // Loading states
    isLoading:
      isLoadingAlerts || isUpdatingAlert || isCreatingAlert || isDeletingAlert,
    isLoadingAlerts,
    isUpdatingAlert,
    isCreatingAlert,
    isDeletingAlert,
    // Error states
    error:
      serverErrorAlerts ||
      serverErrorUpdateAlert ||
      serverErrorCreateAlert ||
      serverErrorDeleteAlert,
    serverErrorAlerts,
    serverErrorUpdateAlert,
    serverErrorCreateAlert,
    serverErrorDeleteAlert,
    // API data
    alertsData,
    updateAlertData,
    createAlertData,
    deleteAlertData,
    // Functions
    addAlert,
    removeAlert,
    updateAlert,
    createAlert,
    fetchAlerts,
    deleteAlert,
    setCurrentAlert,
  };
};

export default useAlerts;
